import { useEffect, useRef } from 'react';

import {
	createPageLoadMetric,
	createPageSegmentLoadMetric,
	getWaterfallTimings,
} from '@confluence/browser-metrics/entry-points/metricConfigs';
import {
	DATABASE_VIEW_PAGE_KEY,
	DATABASE_VIEW_PAGE_CONTENT_SEGMENT_KEY,
} from '@confluence/browser-metrics/entry-points/metric-keys';
import { isInitial, markBrowserMetricEnd } from '@confluence/browser-metrics/entry-points/utils';
import {
	DATABASE_HEADER_SEGMENT_METRIC,
	DATABASE_HEADER_SUBSEGMENT_METRICS,
	OBJECT_HEADER_SEGMENT_METRIC,
} from '@confluence/content-types-header/entry-points/pageSegmentLoadMetrics';
import { SPACE_NAVIGATION_METRIC } from '@confluence/side-navigation/entry-points/pageSegmentLoadMetrics';
import { APP_NAVIGATION_METRIC } from '@confluence/browser-metrics/entry-points/app-navigation.metric';
import { PAGE_TREE_METRIC } from '@confluence/page-tree/entry-points/pageSegmentLoadMetrics';
import { OBJECT_SIDEBAR_CONTROL_METRIC } from '@confluence/object-sidebar-components/entry-points/pageSegmentLoadMetrics';
import { fg } from '@confluence/feature-gating';

export const DATABASE_VIEW_PAGE_CONTENT_SEGMENT = createPageSegmentLoadMetric({
	key: DATABASE_VIEW_PAGE_CONTENT_SEGMENT_KEY,
	ssr: Boolean(window.__SSR_RENDERED__) ? { doneAsFmp: true } : undefined,
});

const NOOP = () => {
	// This is intentional
};

export const getExpectedPageLoadSegments = ({ isInitialLoad }: { isInitialLoad: boolean }) => {
	const segments = [DATABASE_VIEW_PAGE_CONTENT_SEGMENT, DATABASE_HEADER_SEGMENT_METRIC];

	if (fg('confluence_frontend_content_wrapper')) {
		segments.push(OBJECT_SIDEBAR_CONTROL_METRIC);
	}

	if (fg('confluence_frontend_content_wrapper')) {
		segments.push(OBJECT_HEADER_SEGMENT_METRIC);
	}

	if (isInitialLoad) {
		segments.push(APP_NAVIGATION_METRIC, SPACE_NAVIGATION_METRIC, PAGE_TREE_METRIC);
	}
	return segments;
};
// This component assumes startBrowserMetricsPageLoad() has already been called from
// packages/confluence-frontend-server/src/components/Root/App.js in order to generate
// accurate pageLoad start timings. This function is supplementary and starts the
// `until` listeners which will automatically stop the pageLoad.
export const StartDatabasePageLoad = ({ contentId }: { contentId: string }) => {
	const pageLoadMetricRef = useRef<ReturnType<typeof createPageLoadMetric>>();

	useEffect(() => {
		const expectedPageSegments = getExpectedPageLoadSegments({
			isInitialLoad: isInitial(),
		});
		const untilPageSegments = expectedPageSegments.map((segment) => ({
			experience: segment,
			useFmpAsUntilStopTime: false,
		}));
		pageLoadMetricRef.current = createPageLoadMetric({
			key: DATABASE_VIEW_PAGE_KEY,
			timings: getWaterfallTimings(expectedPageSegments),
			include: expectedPageSegments,
			until: untilPageSegments,
		});
		if (!isInitial()) {
			// Make sure all segments are re-started and not currently
			// marked as finished from a previous navigation.

			// Ensure any nested segments are restarted first. If you don't
			// do this with nested segments, you'll see some SPA-navigation
			// perf metrics not get fired (header & main page). You'll also
			// see errors like this if you enable platform browser-metrics
			// package debugging:
			// "BM log:  metric content-types-header has been stopped
			// with stopTime lower than startTime"
			DATABASE_HEADER_SUBSEGMENT_METRICS.forEach((m) => m.startFromPageLoad());
			// then restart the top-level segments
			expectedPageSegments.forEach((m) => m.startFromPageLoad());
		}
		pageLoadMetricRef.current.startPageLoad();
		pageLoadMetricRef.current.onStop(
			markBrowserMetricEnd.bind(null, pageLoadMetricRef.current),
			NOOP,
		);
		// Cleanup on unmount or when dependencies have changed
		return () => {
			pageLoadMetricRef.current?.cancel();
		};
	}, [contentId]);
	return null;
};
